import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Heart, Baby, Plus, MoreHorizontal, BookOpen, Image,
  Sparkles, UsersRound, UserPlus, ChevronRight, Check, GitFork, Feather,
  ArrowRight, Minus,
} from 'lucide-react';

const CHILD_COLORS = [
  { accent: '#E65100', hover: '#C44500', tint: '#FFF6F0' },
  { accent: '#185FA5', hover: '#124B82', tint: '#EFF4FA' },
];

const EXTENDED_COLORS = ['#8B7355', '#7B6B8E', '#6B5B95', '#2A7AB2'];

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const ANON_LABEL = 'A family member';
const AVATAR_COLORS_TO = ['#8B7355', '#7B6B8E', '#6B5B95', '#2A7AB2', '#556B2F', '#8E6B23'];

function FamilyHubMembers({
  groupedFamily,
  familyMembers,
  treeOnlyPeople = [],
  familyStats,
  userPersonId,
  onInvite,
  onAddChild,
}) {
  const navigate = useNavigate();

  if (!groupedFamily) return null;

  const me = groupedFamily.me;
  const spouse = groupedFamily.spouse;
  const children = groupedFamily.children || [];
  // Tree-only people: exclude household members
  const householdIds = new Set([me?.id, spouse?.id, ...children.map(c => c.id)].filter(Boolean));
  const filteredTreeOnly = treeOnlyPeople.filter(p => !householdIds.has(p.id));

  // Extended family: everyone except household AND tree-only people
  const treeOnlyIds = new Set(filteredTreeOnly.map(p => p.id));
  const extended = familyMembers.filter(m =>
    m.id !== me?.id &&
    m.id !== spouse?.id &&
    !children.some(c => c.id === m.id) &&
    !treeOnlyIds.has(m.id)
  );

  const adultCount = familyMembers.filter(m => !m.isMinor).length;
  const childCount = children.length;
  const extendedCount = extended.length;
  const pendingCount = familyStats.pendingInvites || 0;
  const treeOnlyCount = filteredTreeOnly.length;

  return (
    <div style={{ padding: '32px 40px 64px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>

        {/* ── Stat Strip ── */}
        <div style={{
          display: 'flex', background: '#fff', border: '1px solid #E8E4D8',
          borderRadius: 14, boxShadow: '0 1px 2px rgba(54,60,40,.06)',
          overflow: 'hidden', marginTop: 24,
        }}>
          <StatCell number={adultCount} label="Adults" color="#556B2F" />
          <StatCell number={childCount} label="Children" color="#556B2F" border />
          <StatCell number={extendedCount} label="Extended family" color="#556B2F" border />
          <StatCell number={treeOnlyCount} label="Tree only" color="#828A76" border bg="#FBFBF3" />
          <StatCell number={pendingCount} label="Pending invite" color="#E65100" border
            chip={pendingCount > 0 ? { label: 'Waiting', color: '#B5760A', bg: '#FDF0D8', border: '#F5E1B4' } : null}
          />
        </div>

        {/* ── My Household ── */}
        <Section icon={<Home size={18} />} title="My Household" style={{ marginTop: 38 }}>
          <div style={{
            background: '#fff', border: '1px solid #E8E4D8', borderRadius: 16,
            boxShadow: '0 6px 24px rgba(54,60,40,.08), 0 2px 6px rgba(54,60,40,.05)',
            padding: 28,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
              {/* Self */}
              {me && (
                <HouseholdPerson
                  name={`${me.first_name} ${me.last_name}`.trim()}
                  initial={me.first_name?.charAt(0) || 'U'}
                  photo={me.photo || me.photo_url}
                  bg="#556B2F"
                  cardBg="#F0F4E8"
                  cardBorder="#E2EAD2"
                  pillText="You · Storyteller"
                  pillIcon={<Feather size={12} />}
                  pillColor="#556B2F"
                  pillBorder="#E2EAD2"
                  onClick={() => navigate('/my-life')}
                  storyCount={me.story_count}
                  photoCount={me.photo_count}
                />
              )}

              {/* Connector */}
              {spouse && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 'none' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: '#FBE7E3',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Heart size={19} color="#C0432C" />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#828A76' }}>Married</span>
                </div>
              )}

              {/* Spouse */}
              {spouse && (
                <HouseholdPerson
                  name={`${spouse.first_name} ${spouse.last_name}`.trim()}
                  initial={spouse.first_name?.charAt(0) || '?'}
                  photo={spouse.photo || spouse.photo_url}
                  bg="#6B5B95"
                  cardBg="#FAFAF3"
                  cardBorder="#EDE9DC"
                  pillText="Spouse"
                  pillColor="#6B5B95"
                  pillBorder="#E6E0EE"
                  storyCount={spouse.story_count}
                  photoCount={spouse.photo_count}
                  onClick={() => navigate(`/family-hub/member/${spouse.id}`)}
                />
              )}
            </div>
          </div>
        </Section>

        {/* ── Our Children ── */}
        <Section
          icon={<Baby size={18} />}
          title="Our Children"
          count={childCount}
          style={{ marginTop: 38 }}
          action={
            <button
              onClick={onAddChild}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#fff', color: '#556B2F', border: '1px solid #E6E4D8',
                padding: '9px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13.5,
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F5F6EE'; e.currentTarget.style.borderColor = '#D8D6C7'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E6E4D8'; }}
            >
              <Plus size={15} /> Add Child
            </button>
          }
        >
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16,
          }}>
            {children.map((child, idx) => {
              const colorSet = CHILD_COLORS[idx % CHILD_COLORS.length];
              return (
                <ChildCard
                  key={child.id}
                  child={child}
                  colorSet={colorSet}
                  onAddStory={() => navigate(`/tell-a-story?person=${child.id}`)}
                  onViewLife={() => navigate(`/life/${child.id}`)}
                />
              );
            })}

            {/* Add-a-Child card */}
            <button
              onClick={onAddChild}
              style={{
                background: 'transparent', border: '2px dashed #D4D0C0', borderRadius: 16,
                padding: 20, minHeight: 180, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#556B2F'; e.currentTarget.style.background = '#F5F6EE'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#D4D0C0'; e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: 46, height: 46, borderRadius: '50%', background: '#EDE8CD',
                color: '#556B2F', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Plus size={22} />
              </div>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: '#556B2F' }}>Add a Child</span>
              <span style={{ fontSize: 12.5, color: '#828A76' }}>Begin a new life album</span>
            </button>
          </div>
        </Section>

        {/* ── Extended Family ── */}
        {extended.length > 0 && (
          <Section
            icon={<UsersRound size={18} />}
            title="Extended Family"
            style={{ marginTop: 38 }}
            action={
              <button
                onClick={onInvite}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#fff', color: '#556B2F', border: '1px solid #E6E4D8',
                  padding: '9px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13.5,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F5F6EE'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <UserPlus size={15} /> Invite Member
              </button>
            }
          >
            <div style={{
              background: '#fff', border: '1px solid #E8E4D8', borderRadius: 16,
              boxShadow: '0 1px 2px rgba(54,60,40,.06)', overflow: 'hidden',
            }}>
              {extended.map((member, idx) => {
                const avatarColor = EXTENDED_COLORS[idx % EXTENDED_COLORS.length];
                const hasStories = (member.storyCount || 0) > 0;
                // Grace renders identically to member — no special treatment
                const isTreeOnly = !member.hasAccount && !member.isMinor;
                const isPending = !member.hasAccount && !member.invitationAccepted && !member.isMinor;

                return (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '16px 20px',
                      borderBottom: idx < extended.length - 1 ? '1px solid #EFEEE5' : 'none',
                      cursor: 'pointer', transition: 'background .15s',
                    }}
                    onClick={() => navigate(`/life/${member.id}`)}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', background: avatarColor,
                      color: '#fff', fontWeight: 700, fontSize: 16,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden',
                    }}>
                      {(member.photo || member.photo_url) ? (
                        <img src={(member.photo || member.photo_url).startsWith('http') ? (member.photo || member.photo_url) : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${member.photo || member.photo_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (member.firstName?.charAt(0) || '?')}
                    </div>

                    {/* Name + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15.5, color: '#2C2F26' }}>{member.name}</div>
                      <div style={{ fontSize: 13, color: '#828A76' }}>
                        Extended family{hasStories ? ` · ${member.storyCount} stories shared` : ' · on the family tree'}
                      </div>
                    </div>

                    {/* Status chip */}
                    {isPending ? (
                      <StatusChip label="Pending" icon={null} bg="#FDF0D8" color="#B5760A" border="#F5E1B4" />
                    ) : isTreeOnly ? (
                      <StatusChip label="Tree only" icon={<GitFork size={13} />} bg="#F5F3EE" color="#828A76" border="#E6E4D8" />
                    ) : (
                      <StatusChip label="Active" icon={<Check size={13} />} bg="#E7F3E3" color="#2F7A3A" border="#CFE7C8" />
                    )}

                    {/* View their life */}
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/life/${member.id}`); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        color: '#556B2F', fontWeight: 700, fontSize: 13.5,
                        padding: '8px 10px', borderRadius: 8, background: 'transparent',
                        border: 'none', cursor: 'pointer', transition: 'background .15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#F5F6EE'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      View their life <ChevronRight size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* ── Also on your family tree ── */}
        {filteredTreeOnly.length > 0 && (
          <div style={{ paddingTop: 28, borderTop: '1px solid #E2DFCE', marginTop: 28 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <GitFork size={18} color="#828A76" />
                <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#5A6150', margin: 0 }}>
                  Also on your family tree
                </h2>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#828A76', background: '#EDE8CD', padding: '2px 9px', borderRadius: 999 }}>
                  {treeOnlyCount}
                </span>
              </div>
              <button onClick={() => navigate('/family-hub?tab=tree')} style={{
                display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none',
                color: '#556B2F', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: FONT_B,
              }}>
                Open Family Tree <ArrowRight size={14} />
              </button>
            </div>
            <p style={{ fontSize: 14.5, color: '#828A76', fontFamily: FONT_B, marginBottom: 16 }}>
              People recorded on the tree who don't have an account. Their relationships still shape the tree; they can't sign in or share.
            </p>

            {/* Card grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
              {filteredTreeOnly.map((person, idx) => {
                const isFormer = person.former_account;
                const displayName = isFormer ? ANON_LABEL : (person.name || ANON_LABEL);
                const avatarColor = isFormer ? null : AVATAR_COLORS_TO[idx % AVATAR_COLORS_TO.length];

                return (
                  <div key={person.id} style={{
                    background: '#FBFBF3', border: '1px solid #E6E4D8', borderRadius: 16,
                    padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14,
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                      background: isFormer ? '#E4E2D6' : avatarColor,
                      border: isFormer ? '1.5px solid #CFCCBA' : 'none',
                      color: '#fff', fontWeight: 700, fontSize: 18,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isFormer ? null : (person.initials || (person.name || '?')[0].toUpperCase())}
                    </div>

                    {/* Name + relationship */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: FONT_H, fontWeight: 700, fontSize: 17,
                        color: isFormer ? '#8A8A78' : '#2C2F26',
                      }}>
                        {displayName}
                      </div>
                      <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B }}>
                        {person.relationship || 'Family member'}
                      </div>
                    </div>

                    {/* Chip */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: '#F5F3EE', border: '1px solid #E6E4D8', borderRadius: 999,
                      padding: '3px 10px', fontSize: 11.5, fontWeight: 600, color: '#828A76', fontFamily: FONT_B,
                      whiteSpace: 'nowrap',
                    }}>
                      <GitFork size={11} /> Tree only
                    </span>

                    {/* Action: Invite or inert disc */}
                    {isFormer ? (
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%', background: '#F2F0E8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }} title="This person closed their account">
                        <Minus size={14} color="#A8A694" />
                      </div>
                    ) : (
                      <button onClick={() => onInvite && onInvite()} style={{
                        display: 'flex', alignItems: 'center', gap: 4, background: '#fff',
                        border: '1px solid #E6E4D8', borderRadius: 999, padding: '6px 14px',
                        fontSize: 13, fontWeight: 600, color: '#5A6150', cursor: 'pointer',
                        fontFamily: FONT_B, whiteSpace: 'nowrap', minHeight: 34,
                      }}>
                        <UserPlus size={13} /> Invite
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function StatCell({ number, label, color, border, chip, bg }) {
  return (
    <div style={{
      flex: 1, padding: '18px 24px',
      borderRight: border ? '1px solid #EFEEE5' : 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: bg || 'transparent',
    }}>
      <div>
        <div style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 26, lineHeight: 1, color }}>
          {number}
        </div>
        <div style={{ fontSize: 13, color: '#828A76', marginTop: 4 }}>{label}</div>
      </div>
      {chip && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 999,
          background: chip.bg, color: chip.color, border: `1px solid ${chip.border}`,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: chip.color }} />
          {chip.label}
        </span>
      )}
    </div>
  );
}

function Section({ icon, title, count, action, children, style }) {
  return (
    <div style={style}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#556B2F' }}>{icon}</span>
          <h2 style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0 }}>
            {title}
          </h2>
          {count != null && (
            <span style={{
              fontSize: 13, fontWeight: 600, color: '#828A76', background: '#EDE8CD',
              padding: '2px 9px', borderRadius: 999,
            }}>
              {count}
            </span>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function HouseholdPerson({ name, initial, bg, photo, cardBg, cardBorder, pillText, pillIcon, pillColor, pillBorder, onClick, storyCount, photoCount }) {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const photoUrl = photo ? (photo.startsWith('http') ? photo : `${API_BASE}${photo}`) : null;
  const hasStats = (storyCount || 0) > 0 || (photoCount || 0) > 0;
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, minWidth: 240, maxWidth: 380,
        background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14,
        padding: '18px 20px',
        cursor: 'pointer', transition: 'transform .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <div style={{
          width: 60, height: 60, borderRadius: '50%', background: bg,
          color: '#fff', fontWeight: 700, fontSize: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '2.5px solid rgba(255,255,255,.5)', flexShrink: 0, overflow: 'hidden',
        }}>
          {photoUrl ? (
            <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : initial}
        </div>
        <div>
          <div style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 19, color: '#2C2F26' }}>
            {name}
          </div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, fontWeight: 700, color: pillColor,
            background: '#fff', border: `1px solid ${pillBorder}`,
            padding: '3px 10px', borderRadius: 999, marginTop: 6,
          }}>
            {pillIcon} {pillText}
          </span>
        </div>
      </div>
      {hasStats && (
        <div style={{
          marginTop: 12, display: 'flex', gap: 18,
          fontSize: 13.5, color: '#828A76',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <BookOpen size={14} color="#556B2F" /> <strong style={{ color: '#2C2F26' }}>{storyCount || 0}</strong> stories
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Image size={14} color="#556B2F" /> <strong style={{ color: '#2C2F26' }}>{photoCount || 0}</strong> photos
          </span>
        </div>
      )}
    </div>
  );
}

function ChildCard({ child, colorSet, onAddStory, onViewLife }) {
  const hasStories = (child.story_count || 0) > 0;
  const age = child.age != null ? child.age : null;

  return (
    <div style={{
      background: '#fff', border: '1px solid #E8E4D8', borderRadius: 16,
      padding: 20, boxShadow: '0 1px 2px rgba(54,60,40,.06)',
      transition: 'transform .15s, box-shadow .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(54,60,40,.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(54,60,40,.06)'; }}
    >
      {/* Header */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <div style={{
          width: 50, height: 50, borderRadius: '50%', background: colorSet.accent,
          color: '#fff', fontWeight: 700, fontSize: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {child.first_name?.charAt(0) || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 18, color: '#2C2F26' }}>
            {child.first_name} {child.last_name}
          </div>
          {age != null && (
            <div style={{ fontSize: 13, color: '#828A76' }}>Age {age}</div>
          )}
        </div>
        <button style={{
          width: 34, height: 34, borderRadius: 8, background: 'transparent',
          border: 'none', color: '#A9AE9E', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = '#F5F3EE'; e.currentTarget.style.color = '#5A6150'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#A9AE9E'; }}
        >
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* Stats or empty state */}
      {hasStories ? (
        <div style={{
          marginTop: 16, padding: '12px 14px', borderRadius: 10,
          background: colorSet.tint, display: 'flex', gap: 18,
        }}>
          <StatItem icon={<BookOpen size={15} color={colorSet.accent} />} value={child.story_count || 0} label="stories" />
          <StatItem icon={<Image size={15} color={colorSet.accent} />} value={child.photo_count || 0} label="photos" />
        </div>
      ) : (
        <div style={{
          marginTop: 16, padding: '12px 14px', background: '#F5F3EE',
          borderRadius: 10, fontSize: 13.5, color: '#828A76',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Sparkles size={15} color="#B5760A" />
          No stories yet — start their album
        </div>
      )}

      {/* Actions */}
      <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
        <button
          onClick={onAddStory}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: 11, borderRadius: 9, background: colorSet.accent, color: '#fff',
            fontWeight: 700, fontSize: 13.5, border: 'none', cursor: 'pointer',
            transition: 'background .15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = colorSet.hover; }}
          onMouseLeave={e => { e.currentTarget.style.background = colorSet.accent; }}
        >
          <Plus size={15} /> Add Story
        </button>
        <button
          onClick={onViewLife}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: 11, borderRadius: 9, background: '#fff', color: '#5A6150',
            fontWeight: 700, fontSize: 13.5, border: '1px solid #E6E4D8', cursor: 'pointer',
            transition: 'background .15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#F5F6EE'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
        >
          View their life
        </button>
      </div>
    </div>
  );
}

function StatItem({ icon, value, label }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#5A6150' }}>
      {icon} <strong style={{ color: '#2C2F26' }}>{value}</strong> {label}
    </span>
  );
}

function StatusChip({ label, icon, bg, color, border, italic }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 12, fontWeight: italic ? 600 : 700, fontStyle: italic ? 'italic' : 'normal',
      padding: '5px 11px', borderRadius: 999,
      background: bg, color, border: `1px solid ${border}`,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {icon} {label}
    </span>
  );
}

export default FamilyHubMembers;
